import {
  fork,
  all,
  takeLatest,
  select,
  call,
  delay,
  race,
  take,
} from 'redux-saga/effects';
import { SagaIterator } from 'redux-saga';
import {
  done,
  error,
  load,
  setLoadingState,
  start,
  startNextSubTask,
} from './readerTaskOrchestration.slice';
import { selectAuthState } from '../auth/auth.slice';
import { mapUserRoleToTaskEndpoint } from './workers/viewerModeMapper';
import { UserRoles } from '@dynamika-shared/types/auth';
import {
  ReaderTaskOrchestrationLoadAction,
  ReaderTaskOrchestrationStartNextSubTaskAction,
} from './readerTaskOrchestration.types';
import { fetchClinicalTrialContext } from 'src/store-redux/clinicalTrialContext/clinicalTrialContext.slice';
import { fetchClinicalTrialInventory } from 'src/store-redux/clinicalTrialInventory/clinicalTrialInventory.slice';
import {
  fetchReaderTask,
  selectReaderTaskState,
} from 'src/store-redux/readerTask/readerTask.slice';
import { ReaderTaskState } from 'src/store-redux/readerTask/readerTask.types';
import { goToRoute } from '@dynamika-shared/utils/sagaRouter';
import { routesPath } from 'src/router/routes';
import { prepareUrl } from 'src/utils/routerHelpers';
import { fetchClinicalTrialContextWorker } from '../clinicalTrialContext/clinicalTrialContext.saga';
import { fetchClinicalTrialInventoryWorker } from '../clinicalTrialInventory/clinicalTrialInventory.saga';
import { fetchReaderTaskWorker } from '../readerTask/readerTask.saga';
import { formManagementSlice } from '../readerViewerSession/formManagement/formManagement.slice';
import { detachRpcScheme } from 'src/services/rpc/detach/scheme';

import { runToast } from '../tools';
import { prepareCommonData } from './workers/prepareCommonData';
import { handleGroupTaskSaga } from './workers/handleGroupTaskSaga';
import { handleNormalTaskSaga } from './workers/handleNormalTaskSaga';
import { dispatch } from '@dynamika-shared/utils/saga';
import { measurementsSlice } from '../measurements/measurements.slice';
import { getRouteByRole } from 'src/utils/routeHelpers';
import { fetchReaderTask as fetchReaderTaskRequest } from 'src/api/readerTask';
import { Resp } from '../helpers';
import { ChildTask } from '@dynamika-shared/types/reader';
import { findNextEligibleSubTask } from './workers/findNextEligibleSubTask';

function* handleSuccessActions({
  subTaskId,
}: ReaderTaskOrchestrationLoadAction['payload']) {
  const { task, currentVisit } = yield call(prepareCommonData);

  if (!task?.visitId && task?.childTasks?.length) {
    yield call(handleGroupTaskSaga, subTaskId);
  } else if (currentVisit) {
    yield call(handleNormalTaskSaga);
  } else {
    yield call(runToast, 'error', 'No visit found');
  }
}

/**
 * Terminate a reading open that could not load its task.
 *
 * Plain terms
 * -----------
 * A shared reading link can point at a study (clinical trial) the signed-in
 * user simply isn't part of — same job title, but not on this trial. When that
 * happens the server refuses every request for the reading, so we must not leave
 * the user staring at a spinner forever. We show an error and send them back to
 * the normal starting point where they pick their space / role / study, exactly
 * as if they had just logged in.
 *
 * Technical
 * ---------
 * Dispatches the terminal `error` action (loadingState='error' + spinner off)
 * so the viewer page stops rendering the full-page loader, then redirects to
 * `routesPath.home` ('/'). We deliberately do NOT redirect to a trial-scoped
 * list (e.g. `/reader/:id/readings`): when the failure cause is no-access to the
 * clinical trial itself, that list 403s too and would strand the user on a
 * second stuck page. `HomePage` re-runs the standard post-login selection
 * (select-role when multi-role, otherwise the role dashboard), which is the
 * expected recovery flow. No trial id is needed here — hence no `clinicalTrialId`
 * argument.
 */
export function* failReaderOrchestration(): SagaIterator {
  yield dispatch(error());

  yield call(runToast, 'error', 'Unable to open this reading');
  yield call(goToRoute, routesPath.home);
}

/**
 * @function readerTaskOrchestrationSagaWorker
 * @description
 * Manages the lifecycle of loading a clinical trial task. This orchestrator ensures
 * that the application state is clean before fetching new data and handles complex
 * routing for different user roles and window modes (standard vs. detached).
 * * @param {ReaderTaskOrchestrationLoadAction} action - Contains trial, task, and subtask IDs.
 * * @flow-diagram
 * * [ TRIGGER ] -> ReaderTaskOrchestrationLoadAction
 *        |
 *        V
 * +-----------------------+
 * |  PRE-LOAD CLEANUP     | <--- Crucial: Resets form and dirty state
 * |  (Anti-Race Condition)|      immediately to allow fresh initialization.
 * +-----------------------+
 *        |
 *        V
 * +-----------------------+       YES      +----------------------------+
 * |   SHOULD REDIRECT?    |--------------->| Calculate Role-Based Route |
 * +-----------------------+                | (Handle Detached Mode)     |
 *        | NO                              +----------------------------+
 *        V                                              |
 * +-----------------------+                             |
 * | Reset Measurements    | <---------------------------+
 * +-----------------------+
 *        |
 *        V
 * +-----------------------+---------------------------------------------+
 * | DATA FETCHING STRATEGY                                              |
 * | ----------------------                                              |
 * | IF (No data):      Parallel fetch (Task + Context + Inventory)      |
 * | ELSE IF (SubTask): Fetch specific Task data                         |
 * | ELSE IF (New ID):  Fetch specific Task data                         |
 * +---------------------------------------------------------------------+
 *        |
 *        V
 * [ SUCCESS ] -> Fork handleSuccessActions / Set Loading Done
 * * @important
 * * @yields {SagaIterator}
 */
function* readerTaskOrchestrationSagaWorker(
  action: ReaderTaskOrchestrationLoadAction
): SagaIterator {
  const { clinicalTrialId, taskId, subTaskId, shouldRedirect } = action.payload;

  yield dispatch(formManagementSlice.actions.setFormDirty(false));
  yield dispatch(
    formManagementSlice.actions.changeScoringFormData({
      formData: null,
      isValid: false,
    })
  );
  // Reset viewer/measurements gating before initializing a new reading.
  // Unconditional: the redirect branch used to reset via a second
  // `load({ shouldRedirect: false })` enqueued by the route effect, which
  // D7-7685 removed — leaving the previous visit's rows in the slice (D7-7974).
  yield dispatch(measurementsSlice.actions.resetState());

  if (shouldRedirect) {
    const isDetached =
      typeof location !== 'undefined' &&
      location.pathname.includes('viewer-detached');

    let currentRole: UserRoles;
    switch (true) {
      case location.pathname.includes('reader'):
        currentRole = UserRoles.READER;
        break;

      case location.pathname.includes('sponsor'):
        currentRole = UserRoles.SPONSOR;
        break;

      case location.pathname.includes('ext-user'):
        currentRole = UserRoles.EXTERNAL_USER;
        break;

      default:
        currentRole = UserRoles.PROJECT_MANAGER;
    }

    const route = getRouteByRole(
      currentRole,
      isDetached ? 'subTaskViewerDetached' : 'subTaskViewer'
    );

    if (!subTaskId) {
      yield call(goToRoute, prepareUrl(route, [clinicalTrialId, taskId]));
    } else {
      yield call(
        goToRoute,
        prepareUrl(route, [clinicalTrialId, taskId, subTaskId])
      );
    }
    // Do not return here. Continue to the normal loading flow below so that spawn/main tabs actually load the new visit data
  }
  const taskState: ReaderTaskState = yield select(selectReaderTaskState);
  const authState = yield select(selectAuthState);
  const selectedRole = authState?.data?.selectedRole;
  const taskEndpoint = mapUserRoleToTaskEndpoint(selectedRole);
  if (!taskState.data) {
    const authState = yield select(selectAuthState);
    const selectedRole = authState?.data?.selectedRole;
    const taskEndpoint = mapUserRoleToTaskEndpoint(selectedRole);
    yield all([
      call(
        fetchReaderTaskWorker,
        fetchReaderTask({ clinicalTrialId, taskId, endpoint: taskEndpoint })
      ),
      call(
        fetchClinicalTrialContextWorker,
        fetchClinicalTrialContext(clinicalTrialId)
      ),
      call(
        fetchClinicalTrialInventoryWorker,
        fetchClinicalTrialInventory(clinicalTrialId)
      ),
    ]);
    yield call(forkSuccessOrFail, action.payload);
  } else if (subTaskId) {
    yield call(
      fetchReaderTaskWorker,
      fetchReaderTask({ clinicalTrialId, taskId, endpoint: taskEndpoint })
    );
    yield call(forkSuccessOrFail, action.payload);
  } else if (taskState?.data.id !== taskId) {
    yield call(
      fetchReaderTaskWorker,
      fetchReaderTask({ clinicalTrialId, taskId, endpoint: taskEndpoint })
    );
    yield call(forkSuccessOrFail, action.payload);
  } else {
    yield dispatch(setLoadingState('done'));
  }
}

/**
 * Fork the success handler ONLY when the task actually loaded.
 *
 * Plain terms
 * -----------
 * After we ask the server for the reading, we double-check that we really got
 * the reading we asked for before showing it. If the fetch failed (wrong owner,
 * deleted reading, …) the reading is missing, so we stop with a friendly
 * redirect instead of pretending it loaded and spinning forever.
 *
 * Technical
 * ---------
 * `fetchReaderTaskWorker` swallows its own 403/404 (it catches, toasts, and
 * returns the error rather than rethrowing), so `yield all([...])` always
 * resolves and cannot signal failure. We instead re-read `readerTask` from the
 * store: a genuine load leaves `data.id === taskId` (the parent id even on a
 * longitudinal open). Only then do we fork `handleSuccessActions`; otherwise we
 * hand off to `failReaderOrchestration` for a terminal error + redirect.
 */
export function* forkSuccessOrFail(
  payload: ReaderTaskOrchestrationLoadAction['payload']
): SagaIterator {
  const loaded: ReaderTaskState = yield select(selectReaderTaskState);

  if (loaded.data?.id !== payload.taskId) {
    yield call(failReaderOrchestration);
    return;
  }

  yield fork(handleSuccessActions, payload);
}

// Upper bound for a detached tab's next-visit load. If neither `done` nor
// `error` ever arrives (the load's saga was cancelled, or it silently
// returns without a terminal action), we must not hang here forever — the
// main tab would never learn about it either way.
export const NEXT_VISIT_LOAD_TIMEOUT = 30_000;

function isDetachedViewer(): boolean {
  return (
    typeof location !== 'undefined' &&
    location.pathname.includes('viewer-detached')
  );
}

function getReadingsListRouteByRole(role?: UserRoles): string {
  switch (role) {
    case UserRoles.SPONSOR:
      return routesPath.sponsorReadingsCompleted;
    case UserRoles.EXTERNAL_USER:
      return routesPath.externalUserSubjectsClinicalTrial;
    case UserRoles.PROJECT_MANAGER:
    case UserRoles.ADMIN:
    case UserRoles.SUPPORT:
      return routesPath.clinicalTrialOverviewReadingsCompleted;
    case UserRoles.READER:
    default:
      return routesPath.readerClinicalTrialReadings;
  }
}

function* openNextSubTask({
  clinicalTrialId,
  taskId,
  nextSubTask,
  isDetached,
  selectedRole,
}: {
  clinicalTrialId: string;
  taskId: string;
  nextSubTask: ChildTask;
  isDetached: boolean;
  selectedRole?: UserRoles;
}) {
  // `load({ shouldRedirect: true })` owns both navigation and orchestration.
  // Calling goToRoute separately lets the route-param effect enqueue a second
  // load while the state still says `done`, producing duplicate sub-task
  // start requests. The load reducer flips the state to `loading`
  // synchronously, so the route effect stays quiet during this transition.
  yield dispatch(
    load({
      clinicalTrialId,
      taskId,
      subTaskId: nextSubTask.id,
      shouldRedirect: true,
    })
  );

  if (!isDetached) {
    return;
  }

  // Let the detached tab finish starting the next visit before the main tab
  // follows it. Otherwise both tabs can fetch the same PENDING visit and race
  // identical PUT .../start requests. Timeout guards against a lost terminal
  // action (e.g. the load's saga gets cancelled) leaving this worker stuck.
  const { terminal }: {
    terminal?: ReturnType<typeof done> | ReturnType<typeof error>;
  } = yield race({
    terminal: take([done.type, error.type]),
    timeout: delay(NEXT_VISIT_LOAD_TIMEOUT),
  });

  if (terminal?.type !== done.type) {
    return;
  }

  const nextUrl = prepareUrl(
    getRouteByRole(selectedRole ?? UserRoles.READER, 'subTaskViewerDetached'),
    [clinicalTrialId, taskId, nextSubTask.id]
  );

  yield call(detachRpcScheme.events.contextChanged, {
    reason: 'NEXT_SUBTASK_AFTER_SUBMIT',
    clinicalTrialId,
    taskId,
    nextUrl,
  });
}

function* leaveCompletedTaskGroup({
  clinicalTrialId,
  taskId,
  selectedRole,
  isDetached,
}: {
  clinicalTrialId: string;
  taskId: string;
  selectedRole?: UserRoles;
  isDetached: boolean;
}) {
  yield call(runToast, 'info', 'There are no more sub tasks of this reading');
  yield delay(500);

  const listUrl = prepareUrl(getReadingsListRouteByRole(selectedRole), [
    clinicalTrialId,
  ]);

  if (!isDetached) {
    yield call(goToRoute, listUrl);
    return;
  }

  // Symmetric to NEXT_SUBTASK_AFTER_SUBMIT: taskId lets the main tab
  // flush the main tab's parent-task cache, so it does not re-read the
  // pre-submit childTasks statuses if this reading is reopened.
  yield call(detachRpcScheme.events.contextChanged, {
    reason: 'NO_NEXT_READING_AFTER_SUBTASKS',
    clinicalTrialId,
    taskId,
    nextUrl: listUrl,
  });
  yield delay(500);
  window.close();
}

export function* startNextSubTaskWorker(
  action: ReaderTaskOrchestrationStartNextSubTaskAction
): SagaIterator {
  const {
    clinicalTrialId,
    taskId,
    subTaskId: currentSubTaskId,
  } = action.payload;

  const authState = yield select(selectAuthState);
  const selectedRole = authState?.data?.selectedRole;
  const taskEndpoint = mapUserRoleToTaskEndpoint(selectedRole);

  let childTasks: ChildTask[];

  try {
    const response: Resp<typeof fetchReaderTaskRequest> = yield call(
      fetchReaderTaskRequest,
      {
        clinicalTrialId,
        taskId,
        endpoint: taskEndpoint,
      }
    );

    childTasks = response.data.childTasks ?? [];
  } catch {
    yield call(runToast, 'error', 'Unable to refresh the reading visits');
    return;
  }

  const nextSubTask = findNextEligibleSubTask(childTasks, currentSubTaskId);

  if (nextSubTask === undefined) {
    yield call(runToast, 'error', 'Unable to determine the next visit');
    return;
  }

  const isDetached = isDetachedViewer();

  if (nextSubTask) {
    yield* openNextSubTask({
      clinicalTrialId,
      taskId,
      nextSubTask,
      isDetached,
      selectedRole,
    });
    return;
  }

  yield* leaveCompletedTaskGroup({
    clinicalTrialId,
    taskId,
    selectedRole,
    isDetached,
  });
}

export function* readerTaskOrchestrationSaga() {
  yield takeLatest(start.type, readerTaskOrchestrationSagaWorker);
  yield takeLatest(load.type, readerTaskOrchestrationSagaWorker);
  yield takeLatest(startNextSubTask.type, startNextSubTaskWorker);
}
